import { Button, Input, Space, Tooltip } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { Fragment, useEffect, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Dialog as ZhDialog,
    Grid as ZhGrid,
    MultiGrid as ZhMultiGrid
} from "zhihao-ui-react";

import request from "@/api";
import { notifyOperationSuccess } from "@/utils/operationFeedback";
import { useDetailBack } from "@/utils/hooks/useDetailBack";
import { Operation_History_Enum } from "@/meta/logistics/operationHistory";
import { useUserStore } from "@/views/stores";
import AssignDialog, { type assignSubmitData, type Officer } from "@/views/components/AssignDialog/AssignDialog";
import OperationHistoryPanel from "@/views/components/OperationHistoryPanel";
import { ProductDetailBankTable, ProductDetailMedia, ProductDetailRichText } from "./ProductDetailPrimitives";
import ProductDetailMapDialog from "./ProductDetailMapDialog";
import type { GroupPurchaseRow } from "./types";

export interface GroupPurchaseDetailPageProps {
    assignGroupPurchase?: (id: number | string, handlerId: number) => Promise<void>;
    auditGroupPurchase?: (id: number | string, payload: { state: boolean; auditDescn: string }) => Promise<void>;
    closeGroupPurchase?: (id: number | string, unActiveFileIds?: number[]) => Promise<void>;
    downShelfGroupPurchase?: (id: number | string) => Promise<void>;
    findServiceSpecials?: (
        keyword: string
    ) => Promise<Array<{ user: { personId?: number; name?: string; employeeId?: string } }>>;
    loadGroupPurchase?: (id: number | string) => Promise<GroupPurchaseRow>;
    permissions?: string[];
}

/** 复用 Vue 团购商品详情接口。 */
export async function loadGroupPurchaseDetail(id: number | string): Promise<GroupPurchaseRow> {
    const response = await request.service.get<GroupPurchaseRow>(`/admin/product-group-purchase/vo/${id}`);
    return response.data;
}

/** 复用 Vue 团购商品审核接口。 */
export async function auditGroupPurchase(
    id: number | string,
    payload: { state: boolean; auditDescn: string }
): Promise<void> {
    await request.service.put(`/admin/product-group-purchase/audit/${id}`, payload);
}

/** 复用 Vue 团购商品下架接口。 */
export async function downShelfGroupPurchase(id: number | string): Promise<void> {
    await request.service.put(`/admin/product-group-purchase/down-shelf/${id}`);
}

/** 复用 Vue 团购商品关闭接口，并提交需要解除关联的文件 ID。 */
export async function closeGroupPurchase(id: number | string, unActiveFileIds: number[] = []): Promise<void> {
    await request.service.put(`/admin/product-group-purchase/close/${id}`, { unActiveFileIds });
}

/** 复用 Vue 团购商品指派接口。 */
export async function assignGroupPurchase(id: number | string, handlerId: number): Promise<void> {
    await request.service.get(`/admin/product-group-purchase/assign/${id}/${handlerId}`);
}

/** 复用 Vue 团购专员候选查询接口。 */
export async function findGroupPurchaseSpecials(
    keyword: string
): Promise<Array<{ user: { personId?: number; name?: string; employeeId?: string } }>> {
    const response = await request.service.get<
        Array<{ user: { personId?: number; name?: string; employeeId?: string } }>
    >(`/admin/product-group-purchase/find/specials?name=${encodeURIComponent(keyword)}`);
    return response.data;
}

/** 将 Vue 团购详情的团购、商品和发布状态枚举统一为数字。 */
function normalizeGroupPurchaseDetail(row: GroupPurchaseRow): GroupPurchaseRow {
    return {
        ...row,
        productGroupPurchase: {
            ...row.productGroupPurchase,
            groupPurchaseState:
                row.productGroupPurchase.groupPurchaseState == null
                    ? undefined
                    : Number(row.productGroupPurchase.groupPurchaseState),
            state: row.productGroupPurchase.state == null ? undefined : Number(row.productGroupPurchase.state),
            publishState:
                row.productGroupPurchase.publishState == null
                    ? undefined
                    : Number(row.productGroupPurchase.publishState)
        }
    };
}

const productStateLabels: Record<number, string> = { 1: "上架", 2: "下架" };
const publishStateLabels: Record<number, string> = {
    1: "待审核",
    2: "未通过",
    3: "已发布",
    4: "已下架",
    5: "已关闭",
    6: "待提交"
};
const groupStateLabels: Record<number, string> = { 1: "进行中", 2: "拼团成功", 3: "拼团失败" };
const waitingPaymentLabels: Record<number, string> = {
    1: "3小时",
    2: "12小时",
    3: "24小时",
    4: "48小时",
    5: "72小时",
    6: "5天",
    7: "7天",
    8: "不限时长"
};
const paymentTypeLabels: Record<number, string> = { 1: "线上支付", 2: "线下支付", 3: "线上支付、线下支付" };
const taxRateLabels: Record<number, string> = { 1: "3%", 2: "6%", 3: "9%", 4: "13%" };
const priceTypeTips: Record<number, string> = {
    1: "固定价格：为实际交易价格，不受市场行情影响",
    2: "预估价格：价格可能存在变动，以提货当日价格为准"
};

/** 还原 Vue 团购详情的含税枚举文案。 */
function formatTaxValue(isIncludeTax?: number, taxRate?: number): string {
    if (isIncludeTax === 1) return `含税(${taxRateLabels[taxRate || 0] || "--"})`;
    if (isIncludeTax === 0) return "不含税";
    return "--";
}

/** 还原 Vue 团购详情的定金比例/固定金额文案。 */
function formatSettlementValue(product: GroupPurchaseRow["productGroupPurchase"]): string {
    if (product.settlementMethod === 1) return "全部货款";
    if (product.settlementMethod !== 2) return "--";
    const method = product.depositPaymentMethod === 1 ? "货款比例" : "固定金额";
    const value =
        product.depositPaymentMethod === 1 ? `${product.paymentRatio ?? "--"}%` : `${product.fixedAmount ?? "--"}元`;
    return `支付定金: ${method}的${value}`;
}

/** 将旧 Vue 详情头的三段状态按原顺序组合。 */
function getGroupPurchaseStateDescription(product: GroupPurchaseRow["productGroupPurchase"]): string {
    return (
        [
            productStateLabels[product.state || 0],
            publishStateLabels[product.publishState || 0],
            groupStateLabels[product.groupPurchaseState || 0]
        ]
            .filter(Boolean)
            .join(" | ") || "--"
    );
}

/** 将旧 Vue 数值字段保持为带单位的详情文本。 */
function formatWithUnit(value: number | undefined, unit: string): string {
    return value == null ? "--" : `${value}${unit}`;
}

/** 保持旧 Vue 的提货周期展示，兼容接口只返回一端日期的情况。 */
function formatDeliveryPeriod(start?: string, end?: string): string {
    if (start && end) return `${start} 至 ${end}`;
    return start || end || "--";
}

/** 将旧 Vue 参数列表转换成 BaseItem 可直接渲染的文本。 */
function formatParameterInfo(parameterInfo?: GroupPurchaseRow["productGroupPurchase"]["parameterInfo"]): ReactNode {
    if (!parameterInfo?.length) return "--";
    return (
        <div className="html-value product-detail-parameter-value">
            {parameterInfo.map((item, index) => (
                <Fragment key={`${item.parameterName || "parameter"}-${index}`}>
                    {`${item.parameterName || "--"}  ${item.parameterValue || "--"}`}
                    {index < parameterInfo.length - 1 ? <div className="con-width product-detail-con-width" /> : null}
                </Fragment>
            ))}
        </div>
    );
}

/** 将旧 Vue 标签数组转换成详情文本。 */
function formatTags(tags?: string[]): string {
    return tags?.length ? tags.join("、") : "--";
}

/** 将旧 Vue 品类及产区简称保持为列表相同的展示格式。 */
function formatProductType(productType?: GroupPurchaseRow["productType"]): ReactNode {
    if (!productType?.name) return "--";
    return (
        <div className="html-value product-detail-type-value">
            {productType.name}
            {productType.areaAbbreviation ? <span className="split"> ({productType.areaAbbreviation})</span> : null}
        </div>
    );
}

/** 团购商品详情页，展示 Vue 详情页中的交易、提货、结算和服务字段。 */
export function GroupPurchaseDetailPage({
    assignGroupPurchase: assign = assignGroupPurchase,
    auditGroupPurchase: submitAudit = auditGroupPurchase,
    closeGroupPurchase: close = closeGroupPurchase,
    downShelfGroupPurchase: removeFromShelf = downShelfGroupPurchase,
    findServiceSpecials: findSpecials = findGroupPurchaseSpecials,
    loadGroupPurchase = loadGroupPurchaseDetail,
    permissions
}: GroupPurchaseDetailPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const goBack = useDetailBack("/productManagement/groupPurchase");
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const id = new URLSearchParams(location.search).get("id");
    const isAuditPage = location.pathname.endsWith("/audit");
    const currentPermissions = permissions || storePermissions;
    const canDeal = currentPermissions.includes("ROLE_PRODUCT_DEAL");
    const canManage = currentPermissions.includes("ROLE_PRODUCT_MANAGE");
    const canClosePermission = currentPermissions.includes("ROLE_PRODUCT_CLOSE");
    const [detail, setDetail] = useState<GroupPurchaseRow>();
    const [product, setProduct] = useState<GroupPurchaseRow["productGroupPurchase"]>();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [auditState, setAuditState] = useState<boolean | null>(null);
    const [auditDescn, setAuditDescn] = useState("");
    const [auditSubmitting, setAuditSubmitting] = useState(false);
    const [operationLogRefreshToken, setOperationLogRefreshToken] = useState(0);
    const [assignOpen, setAssignOpen] = useState(false);
    const [specialists, setSpecialists] = useState<Awaited<ReturnType<typeof findGroupPurchaseSpecials>>>([]);
    const [selectedHandlerId, setSelectedHandlerId] = useState<number | undefined>();
    const [assignError, setAssignError] = useState<string | null>(null);
    const [assignLoading, setAssignLoading] = useState(false);

    useEffect(() => {
        const previousTitle = document.title;
        document.title = `${isAuditPage ? "审核团购商品" : "团购商品详情"} - 链云砂石`;
        return () => {
            document.title = previousTitle;
        };
    }, [isAuditPage]);

    useEffect(() => {
        if (!id) return;
        void loadGroupPurchase(id)
            .then(result => {
                const normalizedResult = normalizeGroupPurchaseDetail(result);
                setErrorMessage(null);
                setDetail(normalizedResult);
                setProduct(normalizedResult.productGroupPurchase);
            })
            .catch(() => setErrorMessage("团购商品详情加载失败"));
    }, [id, loadGroupPurchase]);

    const refreshDetail = async () => {
        if (!id) return;
        const normalizedResult = normalizeGroupPurchaseDetail(await loadGroupPurchase(id));
        setDetail(normalizedResult);
        setProduct(normalizedResult.productGroupPurchase);
    };

    const handleAudit = async () => {
        if (!id || auditState == null) return;
        if (!auditState && !auditDescn.trim()) {
            setErrorMessage("请填写驳回原因");
            return;
        }
        setAuditSubmitting(true);
        try {
            await submitAudit(id, { state: auditState, auditDescn: auditDescn.trim() });
            navigate("/productManagement/groupPurchase");
        } catch {
            setErrorMessage("团购商品审核失败");
        } finally {
            setAuditSubmitting(false);
        }
    };

    /** 点击审核结果后打开意见弹窗，保持三个商品详情页的审核交互一致。 */
    const openAuditDialog = (state: boolean) => {
        setAuditState(state);
        setAuditDescn("");
        setErrorMessage(null);
    };

    const handleClose = async () => {
        if (!id || !detail) return;
        const unActiveFileIds = [
            detail.mainFile?.id,
            detail.videoFile?.id,
            ...(detail.otherImgFile || []).map(file => file.id)
        ].filter((fileId): fileId is number => fileId != null);
        try {
            await close(id, unActiveFileIds);
            navigate("/productManagement/groupPurchase");
        } catch {
            // 关闭接口失败时保留当前详情，并反馈与 Vue 页面一致的失败状态。
            setErrorMessage("团购商品关闭失败");
        }
    };

    const handleDownShelf = async () => {
        if (!id) return;
        try {
            await removeFromShelf(id);
            await refreshDetail();
            setOperationLogRefreshToken(current => current + 1);
        } catch {
            // 下架或刷新接口失败时保留当前详情，并反馈与 Vue 页面一致的失败状态。
            setErrorMessage("团购商品下架失败");
        }
    };

    /** 查询可指派的商品专员并沿用旧版弹窗的候选列表。 */
    const searchSpecialists = async (keyword: string) => {
        setAssignLoading(true);
        try {
            setAssignError(null);
            setSpecialists(await findSpecials(keyword));
        } catch {
            setAssignError("专员查询失败");
            setSpecialists([]);
        } finally {
            setAssignLoading(false);
        }
    };

    /** 打开指派弹窗，并默认选中当前商品专员。 */
    const openAssign = () => {
        if (!product) return;
        setAssignOpen(true);
        setSelectedHandlerId(product.handlerId);
        void searchSpecialists("");
    };

    /** 提交商品专员变更，并刷新详情和操作日志。 */
    const handleAssign = async ({ officerId }: assignSubmitData) => {
        if (!id || officerId == null) {
            setAssignError("请选择指派的服务人员");
            return;
        }
        setAssignLoading(true);
        try {
            await assign(id, officerId);
            notifyOperationSuccess("变更指派成功");
            setAssignOpen(false);
            await refreshDetail();
            setOperationLogRefreshToken(current => current + 1);
        } catch {
            setAssignError("变更指派失败");
        } finally {
            setAssignLoading(false);
        }
    };

    return (
        <section aria-label="group-purchase-detail-page" className="detail-page-transparent admin-detail-page">
            <AssignDialog
                open={assignOpen}
                info={detail || undefined}
                title="变更指派商品专员"
                officers={specialists as Officer[]}
                searchSpecial={async keyword => {
                    await searchSpecialists(keyword);
                    return [];
                }}
                tips="跟进的商品数量"
                countFiled="productCount"
                countSuffix="个"
                curOfficerId={selectedHandlerId}
                confirmText="确认"
                loading={assignLoading}
                error={assignError}
                onClose={() => {
                    setAssignOpen(false);
                    setAssignError(null);
                }}
                onCurOfficerIdChange={setSelectedHandlerId}
                onSubmit={handleAssign}
            />
            <div className="section-card admin-detail-header-card">
                <ZhDetailHeader
                    leftInfos={[
                        { label: "商品编号", value: product?.id || "--" },
                        { label: "商品专员", value: product?.handlerName || "--" }
                    ]}
                    stateDesc={product ? getGroupPurchaseStateDescription(product) : "--"}
                    title={isAuditPage ? "审核团购商品" : "团购商品详情"}
                />
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
            </div>
            {product ? (
                <ZhMultiGrid>
                    <section aria-label="商品信息" className="section-card admin-detail-section product-detail-section">
                        <div className="section-title mb-16">商品信息</div>
                        <ZhGrid cols={4} gap={[20, 20]}>
                            <ZhBaseItem colon={false} label="商品标题" labelWidth={84} value={product.title || "--"} />
                            <ZhBaseItem colon={false} label="权重值" labelWidth={112} value={product.weight ?? "--"} />
                            <ZhBaseItem
                                label="拼团单价"
                                labelWidth={84}
                                value={
                                    <>
                                        <span>{formatWithUnit(product.groupUnitPrice, "元/吨")}</span>
                                        {product.priceType ? (
                                            <Tooltip title={priceTypeTips[product.priceType]} placement="top">
                                                <span className="product-detail-price-type price-type-tips">
                                                    <i aria-hidden="true" className="iconfont icon-a-Frame38" />
                                                    <span>{product.priceType === 1 ? "固定价格" : "预估价格"}</span>
                                                </span>
                                            </Tooltip>
                                        ) : null}
                                    </>
                                }
                            />
                            <ZhBaseItem
                                label="是否含税"
                                labelWidth={84}
                                value={formatTaxValue(product.isIncludeTax, product.taxRate)}
                            />
                            <ZhBaseItem
                                label="原价"
                                labelWidth={84}
                                value={formatWithUnit(product.unitPrice, "元/吨")}
                            />
                            <ZhBaseItem label="拼团截止时间" labelWidth={112} value={product.groupEndTime || "--"} />
                            <ZhBaseItem
                                label="等待付款时间"
                                labelWidth={84}
                                value={waitingPaymentLabels[product.waitingPayTime || 0] || "--"}
                            />
                            <ZhBaseItem
                                label="拼团最低吨数"
                                labelWidth={84}
                                value={formatWithUnit(product.minOrderTon, "吨")}
                            />
                            <ZhBaseItem
                                label="拼团成功吨数"
                                labelWidth={84}
                                span={4}
                                value={formatWithUnit(product.groupSuccessTon, "吨")}
                            />
                            <ProductDetailMedia
                                imagePath={detail?.mainFile?.path}
                                videoPath={detail?.videoFile?.path}
                            />
                            <ZhBaseItem
                                label="供应商"
                                labelWidth={84}
                                value={
                                    product.supplierEnterprise?.enterpriseName ||
                                    product.supplierEnterprise?.supplierName ||
                                    "--"
                                }
                            />
                            <ZhBaseItem label="品类" labelWidth={112} value={formatProductType(detail?.productType)} />
                            <ZhBaseItem label="规格" labelWidth={84} value={product.productTypeSpec || "--"} />
                            <ZhBaseItem
                                label="品类标签"
                                labelWidth={84}
                                value={formatTags(
                                    detail?.productTypeVo?.tags?.map(tag => tag.name).filter(Boolean) as string[]
                                )}
                            />
                            <ZhBaseItem
                                label="参数值"
                                labelWidth={84}
                                span={4}
                                value={formatParameterInfo(product.parameterInfo)}
                            />
                            <ZhBaseItem
                                label="商品标签"
                                labelWidth={84}
                                span={4}
                                value={formatTags(product.productLabel)}
                            />
                            <ZhBaseItem
                                label="商品介绍"
                                labelWidth={84}
                                span={4}
                                value={<ProductDetailRichText value={product.introduce} />}
                            />
                        </ZhGrid>
                    </section>
                    <section aria-label="提货信息" className="section-card admin-detail-section product-detail-section">
                        <div className="section-title mb-16">提货信息</div>
                        <ZhGrid cols={4} gap={[20, 20]}>
                            <ZhBaseItem
                                label="提货周期"
                                labelWidth={84}
                                value={formatDeliveryPeriod(product.deliveryStartTime, product.deliveryEndTime)}
                            />
                            <ZhBaseItem
                                label="最小调整吨数"
                                labelWidth={112}
                                value={formatWithUnit(product.minAdjustmentTon, "吨")}
                            />
                            <ProductDetailMapDialog labelWidth={84} location={product} />
                            <ZhBaseItem
                                label="提货说明"
                                labelWidth={84}
                                span={4}
                                value={<ProductDetailRichText value={product.deliveryDescn} />}
                            />
                        </ZhGrid>
                    </section>
                    <section aria-label="结算信息" className="section-card admin-detail-section product-detail-section">
                        <div className="section-title mb-16">结算信息</div>
                        <ZhGrid cols={4} gap={[20, 20]}>
                            <ZhBaseItem label="下单要求" labelWidth={84} value={formatSettlementValue(product)} />
                            <ZhBaseItem
                                label="支付方式"
                                labelWidth={112}
                                value={paymentTypeLabels[product.payType || 0] || "--"}
                            />
                            <ZhBaseItem
                                label="结算说明"
                                labelWidth={84}
                                span={4}
                                value={<ProductDetailRichText value={product.settlementDescn} />}
                            />
                        </ZhGrid>
                        <ProductDetailBankTable accounts={product.accountInfo} payType={product.payType} />
                    </section>
                    <section aria-label="服务保障" className="section-card admin-detail-section product-detail-section">
                        <div className="section-title mb-16">服务保障</div>
                        <ZhGrid cols={1} gap={[20, 20]}>
                            <ZhBaseItem
                                label="服务保障"
                                labelWidth={84}
                                span={4}
                                value={<ProductDetailRichText value={product.serviceGuarantee} />}
                            />
                            <ZhBaseItem
                                label="团购规则"
                                labelWidth={84}
                                span={4}
                                value={<ProductDetailRichText value={product.regulation} />}
                            />
                        </ZhGrid>
                    </section>
                    <section aria-label="其他信息" className="section-card admin-detail-section product-detail-section">
                        <div className="section-title mb-16">其他信息</div>
                        <ZhGrid cols={4} gap={[20, 20]}>
                            <ZhBaseItem label="商品专员" labelWidth={84} value={product.handlerName || "--"} />
                            <ZhBaseItem
                                label="商品专员联系电话"
                                labelWidth={112}
                                span={3}
                                value={product.handlerMobile?.join("; ") || "--"}
                            />
                        </ZhGrid>
                    </section>
                </ZhMultiGrid>
            ) : null}
            {detail ? (
                <OperationHistoryPanel
                    bizNo={id || detail.productGroupPurchase.id || ""}
                    module={Operation_History_Enum.PRODUCT_GROUP_PURCHASE_DETAIL}
                    refreshToken={operationLogRefreshToken}
                />
            ) : null}
            <Space className="operation-fixed-btn" size={0} wrap>
                {isAuditPage ? (
                    <>
                        <Button aria-label="同意" onClick={() => openAuditDialog(true)}>
                            同意
                        </Button>
                        <Button aria-label="拒绝" onClick={() => openAuditDialog(false)}>
                            拒绝
                        </Button>
                    </>
                ) : detail ? (
                    <>
                        {canManage && product && !(product.state === 2 && product.publishState === 5) ? (
                            <Button aria-label="变更指派" onClick={openAssign}>
                                变更指派
                            </Button>
                        ) : null}
                        {canManage && product?.publishState === 1 ? (
                            <Button
                                aria-label="审核团购商品"
                                onClick={() => void navigate(`/productManagement/groupPurchase/audit?id=${id || ""}`)}
                            >
                                审核
                            </Button>
                        ) : null}
                        {canDeal && product?.state === 2 && [1, 2, 4, 5, 6].includes(product.publishState || 0) ? (
                            <Button
                                aria-label="编辑团购商品"
                                onClick={() => void navigate(`/productManagement/groupPurchase/edit?id=${id || ""}`)}
                            >
                                编辑
                            </Button>
                        ) : null}
                        {canClosePermission &&
                        product?.state === 2 &&
                        [1, 2, 4, 5, 6].includes(product.publishState || 0) ? (
                            <Button aria-label="关闭团购商品" onClick={() => void handleClose()}>
                                关闭
                            </Button>
                        ) : null}
                        {canDeal &&
                        product?.state === 1 &&
                        [2, 3].includes(product.publishState || 0) &&
                        product.groupPurchaseState === 1 ? (
                            <Button aria-label="下架团购商品" onClick={() => void handleDownShelf()}>
                                下架
                            </Button>
                        ) : null}
                        {canDeal &&
                        product?.state === 2 &&
                        product.publishState === 5 &&
                        [2, 3].includes(product.groupPurchaseState || 0) ? (
                            <Button
                                aria-label="复制团购商品"
                                onClick={() => void navigate(`/productManagement/groupPurchase/add?copyId=${id || ""}`)}
                            >
                                复制
                            </Button>
                        ) : null}
                    </>
                ) : null}
                <Button onClick={goBack}>返回</Button>
            </Space>
            {isAuditPage ? (
                <ZhDialog
                    open={auditState != null}
                    title={auditState ? "审核意见" : "驳回原因"}
                    width={400}
                    onCancel={() => setAuditState(null)}
                    footer={
                        <Space>
                            <Button onClick={() => setAuditState(null)}>取消</Button>
                            <Button
                                aria-label="确认"
                                loading={auditSubmitting}
                                type="primary"
                                onClick={() => void handleAudit()}
                            >
                                确认
                            </Button>
                        </Space>
                    }
                >
                    {errorMessage === "请填写驳回原因" ? (
                        <Alert message={errorMessage} role="alert" type="error" />
                    ) : null}
                    <Input.TextArea
                        aria-label={auditState ? "审核意见" : "驳回原因"}
                        maxLength={200}
                        placeholder={auditState ? "请输入审核意见" : "请输入驳回原因"}
                        rows={4}
                        value={auditDescn}
                        onChange={event => setAuditDescn(event.target.value)}
                    />
                </ZhDialog>
            ) : null}
        </section>
    );
}

export default GroupPurchaseDetailPage;
